// src/pages/DailyChallengePage.js
import React, { useState, useEffect } from 'react';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import LevelEditor from '../components/LevelEditor'; // <--- Import Editor
import './DailyChallengePage.css';

const DailyChallengePage = () => {
    const [loading, setLoading] = useState(false);
    const [challengeData, setChallengeData] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [sendNotification, setSendNotification] = useState(false);

    // State để lưu level đang xem chi tiết
    const [viewingLevel, setViewingLevel] = useState(null);

    useEffect(() => {
        const unsub = onSnapshot(doc(db, "daily_challenges", selectedDate), (doc) => {
            if (doc.exists()) {
                setChallengeData(doc.data());
            } else {
                setChallengeData(null);
            }
        });
        return () => unsub();
    }, [selectedDate]);

    const handleGenerate = async () => {
        setLoading(true);
        try {
            const generateFn = httpsCallable(functions, 'adminGenerateChallenge');
            await generateFn({ date: selectedDate, isSendNotification: sendNotification });
            alert("Đã tạo Daily Challenge thành công!");
        } catch (error) {
            console.error(error);
            alert("Lỗi: " + error.message);
        }
        setLoading(false);
    };

    // --- HÀM CHUYỂN ĐỔI DỮ LIỆU ---
    // Convert từ format Daily (rows/cols/grid[]) sang format Editor (width/height/flatGrid[])
    const openLevelDetail = (levelData, difficulty) => {
        if (!levelData) return;

        const width = levelData.cols;
        const height = levelData.rows;

        // 1. Tạo mảng phẳng rỗng
        const flatGrid = Array.from({ length: width * height }, () => ({ n: 0, b: [] }));

        // 2. Map dữ liệu vào
        levelData.grid.forEach(cell => {
            const index = cell.row * width + cell.col;
            flatGrid[index] = {
                n: cell.number || 0,
                b: cell.borders || []
            };
        });

        // 3. Tạo object Puzzle hoàn chỉnh cho Editor
        const editorData = {
            puzzleId: `daily_${selectedDate}_${difficulty}`,
            difficulty: difficulty,
            level: 0, // Daily không có số level
            width: width,
            height: height,
            flatGrid: flatGrid,
            solutionPath: levelData.solutionPath || [],
            hints: [] // Daily challenge thường không dùng mảng hints rời mà dùng số trên grid
        };

        setViewingLevel(editorData);
    };

    // Nếu đang xem chi tiết -> Hiển thị Editor
    if (viewingLevel) {
        return (
            <div className="daily-page">
                <div className="editor-wrapper">
                    <button className="btn-back" onClick={() => setViewingLevel(null)}>
                        ← Back to List
                    </button>
                    <LevelEditor
                        initialData={viewingLevel}
                        onSave={() => alert("Daily Challenge generated automatically. Saving manual edits is not supported yet.")}
                        onCancel={() => setViewingLevel(null)}
                    // getNextLevel={...} // Không cần thiết cho Daily
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="daily-page">
            <h1>Daily Challenge Manager</h1>

            <div className="controls">
                <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                />
                <label className="checkbox-label">
                    <input
                        type="checkbox"
                        checked={sendNotification}
                        onChange={(e) => setSendNotification(e.target.checked)}
                    />
                    Gửi noti cho user
                </label>
                <button onClick={handleGenerate} disabled={loading} className="btn-generate">
                    {loading ? "Generating..." : "Generate New Challenge"}
                </button>
            </div>

            {!challengeData ? (
                <div className="empty-state">Chưa có dữ liệu cho ngày {selectedDate}</div>
            ) : (
                <div className="levels-container">
                    {/* Truyền hàm onClick để mở chi tiết */}
                    <LevelPreview
                        title="Easy"
                        data={challengeData.levels.easy}
                        onClick={() => openLevelDetail(challengeData.levels.easy, 'easy')}
                    />
                    <LevelPreview
                        title="Medium"
                        data={challengeData.levels.medium}
                        onClick={() => openLevelDetail(challengeData.levels.medium, 'medium')}
                    />
                    <LevelPreview
                        title="Hard"
                        data={challengeData.levels.hard}
                        onClick={() => openLevelDetail(challengeData.levels.hard, 'hard')}
                    />
                </div>
            )}
        </div>
    );
};

// Component con hiển thị preview (Thêm onClick)
const LevelPreview = ({ title, data, onClick }) => {
    if (!data) return null;
    const { rows, cols, grid } = data;

    // Tạo mảng 2 chiều để render preview
    const displayGrid = Array(rows).fill(null).map(() => Array(cols).fill(null));
    grid.forEach(cell => {
        displayGrid[cell.row][cell.col] = cell;
    });

    return (
        <div className="level-preview" onClick={onClick}>
            <div className="preview-header">
                <h3>{title} ({rows}x{cols})</h3>
                <span className="click-hint">Click to View</span>
            </div>

            <div
                className="mini-grid"
                style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
            >
                {displayGrid.map((row, r) => (
                    row.map((cell, c) => {
                        const borders = cell?.borders || [];
                        const borderClass = borders.map(b => `border-${b}`).join(' ');

                        return (
                            <div key={`${r}-${c}`} className={`mini-cell ${!cell ? 'empty' : ''} ${borderClass}`}>
                                {cell?.number > 0 && <span className="number">{cell.number}</span>}
                            </div>
                        );
                    })
                ))}
            </div>
        </div>
    );
};

export default DailyChallengePage;